import { getAccessTokenCookie } from "./authToken";

export const TEACHER_SESSION_ERROR_CODES = {
  ACCESS_DENIED: "ACCESS_DENIED",
  AUTH_REQUIRED: "AUTH_REQUIRED",
  SESSION_UNAVAILABLE: "SESSION_UNAVAILABLE",
};

export class TeacherSessionError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "TeacherSessionError";
    this.code = code;
  }
}

const decodeBase64Url = (value) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  return atob(`${base64}${padding}`);
};

const decodeAccessToken = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Unexpected token shape");
    }
    return JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.AUTH_REQUIRED,
      "Your dashboard session is invalid. Sign in again.",
    );
  }
};

const getExpectedIssuer = () => {
  const region = process.env.REACT_APP_COGNITO_REGION;
  const userPoolId = process.env.REACT_APP_USER_POOL_ID;
  return region && userPoolId
    ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`
    : null;
};

const claimIsTrue = (value) => value === true || value === "true";

const EMPTY_PERMISSIONS = {
  viewAdminDashboard: false,
  viewWebContent: false,
  viewVrContent: false,
  viewDashboard: false,
  viewAnalytics: false,
  crudGuides: false,
  crudExperiences: false,
  crudContent: false,
  crudAccounts: false,
};

const normalizePermissions = (permissions) => {
  if (permissions && typeof permissions === "object") {
    return { ...EMPTY_PERMISSIONS, ...permissions };
  }
  if (typeof permissions === "string") {
    try {
      const parsed = JSON.parse(permissions);
      return parsed && typeof parsed === "object"
        ? { ...EMPTY_PERMISSIONS, ...parsed }
        : { ...EMPTY_PERMISSIONS };
    } catch {
      return { ...EMPTY_PERMISSIONS };
    }
  }
  return { ...EMPTY_PERMISSIONS };
};

const getDisplayName = (profile) =>
  [profile?.firstName, profile?.middleName, profile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  profile?.email ||
  "Teacher";

const getResponsePayload = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const validateTeacherSession = async ({
  token = getAccessTokenCookie(),
  fetchImpl,
  now = Date.now(),
} = {}) => {
  if (!token) {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.AUTH_REQUIRED,
      "Sign in through the 10k dashboard to view this experience.",
    );
  }

  const claims = decodeAccessToken(token);
  const expectedIssuer = getExpectedIssuer();
  const expectedClientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
  const expiresAtMs = Number(claims.exp) * 1000;

  if (
    !expectedIssuer ||
    !expectedClientId ||
    claims.iss !== expectedIssuer ||
    claims.client_id !== expectedClientId ||
    claims.token_use !== "access" ||
    !Number.isFinite(expiresAtMs) ||
    expiresAtMs <= now
  ) {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.AUTH_REQUIRED,
      "Your dashboard session has expired. Sign in again.",
    );
  }

  const userId = String(claims.userId || "").trim();
  const accountsBaseUrl = process.env.REACT_APP_API_ACCOUNTS_URL;
  const fetcher =
    fetchImpl ||
    (typeof window !== "undefined" && typeof window.fetch === "function"
      ? window.fetch.bind(window)
      : null);
  if (!userId || !accountsBaseUrl || typeof fetcher !== "function") {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.SESSION_UNAVAILABLE,
      "The dashboard session could not be verified.",
    );
  }

  let response;
  try {
    response = await fetcher(
      `${accountsBaseUrl.replace(/\/$/, "")}/users/${encodeURIComponent(userId)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        method: "GET",
      },
    );
  } catch {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.SESSION_UNAVAILABLE,
      "The dashboard session could not be verified.",
    );
  }

  const profile = await getResponsePayload(response);
  if (!response.ok) {
    const authFailure = response.status === 401 || response.status === 403;
    throw new TeacherSessionError(
      authFailure
        ? TEACHER_SESSION_ERROR_CODES.AUTH_REQUIRED
        : TEACHER_SESSION_ERROR_CODES.SESSION_UNAVAILABLE,
      authFailure
        ? "Your dashboard session has expired. Sign in again."
        : "The dashboard session could not be verified.",
    );
  }

  if (!profile?.id || String(profile.id) !== userId) {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.AUTH_REQUIRED,
      "The authenticated account did not match this session.",
    );
  }

  if (!claimIsTrue(claims.hasClassroomAccess)) {
    throw new TeacherSessionError(
      TEACHER_SESSION_ERROR_CODES.ACCESS_DENIED,
      "This account does not have access to the web viewer.",
    );
  }

  const name = getDisplayName(profile);
  const permissions = normalizePermissions(claims.permissions);
  return {
    expiresAt: new Date(expiresAtMs).toISOString(),
    userData: {
      name,
      email: profile.email || "",
      id: userId,
      jwt: token,
      refreshToken: "",
      admin:
        claims.roleName === "admin" || permissions.viewAdminDashboard === true,
      organizationId: claims.organizationId || profile.organizationId || "",
      organizationName: claims.organizationName || "",
      subscriptionStatus:
        claims.subscriptionStatus || profile.subscription?.type || "free",
      permissions,
      displayName: {
        first: profile.firstName || name,
        middle: profile.middleName || "",
        last: profile.lastName || "",
      },
    },
  };
};
